import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FAQPage } from 'schema-dts';
import { JsonLd } from 'react-schemaorg';

import { Accordion, AccordionItem } from '../../core/Accordion';
import Button from '../../core/Button';
import Typography from '../../core/Typography';

import useStyles from './styles';
import IFaqCollapsedSection from './types';

function FaqCollapsedSection({
  title,
  titleElement = 'h2',
  description,
  questions,
  className,
  label,
}: IFaqCollapsedSection) {
  const classes = useStyles();

  const [questionsVisible, setQuestionsVisible] = useState<number>(3);

  const loadAll = useCallback(() => {
    if (questionsVisible >= questions.length) {
      return;
    }
    setQuestionsVisible(questions.length);
  }, [questions.length, questionsVisible]);

  const showSchemaWarning =
    questions.findIndex(
      ({ answer, answerSchema }) => typeof answer !== 'string' && !answerSchema,
    ) !== -1;

  if (showSchemaWarning) {
    return <div>FAQ Schema org is invalid</div>;
  }

  return (
    <section className={classNames(classes.container, className, 'landing-banner')}>
      <div className={classes.root}>
        <div className={classes.text}>
          {label && (
            <Typography className={classes.label} element="h3" look="p">
              {label}
            </Typography>
          )}
          <Typography className={classes.title} element={titleElement}>
            {title}
          </Typography>
          <Typography className={classes.description} element="p" color="var(--x-text-color-2)">
            {description}
          </Typography>
        </div>
        <div className={classes.questions}>
          <Accordion className={classes.accordion} multiple={true}>
            {questions.map((question, index) => (
              <AccordionItem
                className={index >= questionsVisible && classes.hiddenQuestion}
                key={index}
                toggleContent={
                  <Typography className={classes.questionTitle} element="h3">
                    {question.question}
                  </Typography>
                }
              >
                <Typography
                  className={classes.questionAnswer}
                  element="p"
                  color="var(--x-text-color-2)"
                >
                  {question.answer}
                </Typography>
              </AccordionItem>
            ))}
          </Accordion>
          {questionsVisible < questions.length && (
            <Button className={classes.ctaButton} onClick={loadAll}>
              View all questions
            </Button>
          )}
        </div>
      </div>

      <JsonLd<FAQPage>
        item={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: questions.map((question) => ({
            '@type': 'Question',
            name: question.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: typeof question.answer !== 'string' ? question.answerSchema : question.answer,
            },
          })),
        }}
      />
    </section>
  );
}

export default FaqCollapsedSection;
